import React, { useState, useEffect, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form, Button, message, Select, Space, Collapse } from "antd";
import Breadcrumb from "../../../components/Breadcrumb";
import IndexOrderTableElement from "../../../components/Application/DefaultIndexOrderSettings/IndexOrderTableElement";
// get solutions api react query calls
import {
  useGetAllSolutions,
  useGetDefaultIndex,
  useGetDefaultIndexOrderSettings,
  useSetDefaultIndexOrderSettings,
} from "../../../hooks/solutions.hook";
// Application api react query calls
import { useGetAllApplications } from "../../../hooks/applications.hooks";
// get indexes api react query calls
import { useGetAllIndexes } from "../../../hooks/indexes.hooks";
import { DefaultIndexOrderSettingsSchema } from "../../../schema/DefaultIndexOrderSettings.schema";
import { SolutionSlugs } from "../../../utils/solutions";

const { Option } = Select;
const { Panel } = Collapse;

const itemStyles = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 6 },
    md: { span: 6 },
    lg: { span: 5 },
    xl: { span: 5 },
    xxl: { span: 5 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 18 },
    md: { span: 18 },
    lg: { span: 15 },
    xl: { span: 12 },
    xxl: { span: 12 },
  },
};
const AppList = [
  { AppName: "ULP" },
  { AppName: "QMS" },
  { AppName: "myDocs" },
  { AppName: "Compliance" },
];

// INFO: highly advise to study javascript funcamentals. can identify same issue occuring again and again.
// cause for table duplication issue is clearly visible within the page. could you please check for one last time whether you can identify the issue.

const DefaultIndexOrderSettings = () => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(DefaultIndexOrderSettingsSchema),
    mode: "onChange",
  });

  // console.log(errors, "errors");
  const { fields, replace } = useFieldArray({
    control,
    name: "index_order",
  });

  const [solutionID, setSolutionID] = useState(undefined);
  const [applicationIds, setApplicationIds] = useState([]);

  // Get Solution Data
  const { isLoading: isLoadingSolutionsData, data: SolutionsData } =
    useGetAllSolutions({
      select: (dataList) => {
        let SolutionData;
        if (dataList !== undefined) {
          SolutionData = dataList.sort((a, b) =>
            a.solution_slug.localeCompare(b.solution_slug),
          );
        }
        return SolutionData;
      },
      onError: () => message.error("Server Error"),
    });

  // Get Application List
  const { isLoading: isLoadingApplicationData, data: ApplicationData } =
    useGetAllApplications({
      select: (dataList) => {
        let Applications;
        if (dataList !== undefined) {
          Applications = dataList.sort((a, b) =>
            a.app_name.localeCompare(b.app_name),
          );
        }
        return Applications;
      },
      onError: () => message.error("Server Error"),
    });

  // Get Index Data
  const { data: IndexData } = useGetAllIndexes({
    select: (dataList) => {
      let Index; // TODO: incorrect variable name. function returns array of index instead of single index element.
      if (dataList !== undefined) {
        Index = dataList
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((data) => {
            return {
              _id: data._id,
              name: data.name,
            };
          });
      }
      return Index;
    },
    onError: () => message.error("Server Error"),
  });

  // Get Default Index Data
  const { data: GetDefaultIndexData } = useGetDefaultIndex(solutionID, {
    select: (dataList) => {
      let defaultIndex; // TODO: incorrect variable name. function returns array of elements instead of single element.
      if (dataList !== undefined) {
        defaultIndex = dataList.map((data) => data.index_id);
      }
      return defaultIndex;
    },
    onError: () => message.error("Server Error"),
  });

  // Get Default Index Order Data
  const { data: GetDefaultIndexOrderData } = useGetDefaultIndexOrderSettings(
    solutionID,
    {
      cacheTime: 0,
      onError: () => message.error("Server Error"),
    },
  );

  // const SolutionsList = useMemo(() => {
  //   // TODO: no need to define a new variable. write the logic in the render function - change added
  //   if (SolutionsData !== undefined) {
  //     return SolutionsData.sort(
  //       (a, b) => a.solution_slug.localeCompare(b.solution_slug), // TODO: can be managed using react query options
  //     ).map((solution) => (
  //       <Option
  //         key={solution._id}
  //         value={solution._id}
  //         disabled={
  //           !SolutionSlugs.find(
  //             (solutionSlug) =>
  //               solutionSlug.solutionSlug === solution.solution_slug,
  //           )
  //         }
  //       >
  //         {solution.solution_name}
  //       </Option>
  //     ));
  //   }
  // }, [SolutionsData]);

  // Generate Application List to Show in Select
  // const ApplicationList = ApplicationData?.sort((a, b) => {// TODO: no need to define a new variable. write the logic in the render function -change added
  //   return a._id.localeCompare(b._id);// TODO: can be managed using react query options
  // })?.map((application) => {
  //   return (
  //     <Option
  //       key={application._id}
  //       value={application._id}
  //       disabled={
  //         application.app_name !== "ULP" && // TODO: enabled aps should be seperated into a constant
  //         application.app_name !== "QMS" &&
  //         application.app_name !== "Compliance" &&
  //         application.app_name !== "myDocs"
  //       }
  //     >
  //       {application?.app_name}
  //     </Option>
  //   );
  // });

  useEffect(() => {
    if (GetDefaultIndexOrderData !== undefined) {
      let data = [];
      GetDefaultIndexOrderData.forEach((element) => {
        data.push(element.app_id);
      });
      setApplicationIds(data);
    }
  }, [GetDefaultIndexOrderData]);

  const relevantApps = useMemo(
    () =>
      ApplicationData?.filter((app) => applicationIds?.includes(app._id)).map(
        (index) => {
          return {
            _id: index._id,
            app_name: index.app_name,
          };
        },
      ),
    [applicationIds],
  );

  useEffect(() => {
    const relevantIndexes = IndexData?.filter((getDefaultIndex) =>
      GetDefaultIndexData?.includes(getDefaultIndex._id),
    ).map((index) => {
      return {
        index_id: index._id,
        name: index.name,
        order: null,
      };
    });

    if (relevantApps !== undefined) {
      let relevantMapping = [];
      relevantApps.forEach((app) => {
        let relevantDefaultIndexSetOrder = GetDefaultIndexOrderData?.find(
          (GetDefaultIndexOrder) => app._id === GetDefaultIndexOrder.app_id,
        );
        const IndexMappingData = relevantIndexes?.map((index) => {
          const relevantDefaultIndex =
            relevantDefaultIndexSetOrder?.index_order?.find(
              (getDefaultIndexOrder) =>
                getDefaultIndexOrder?.index_id === index.index_id,
            );

          return {
            index_id: index.index_id,
            name: index.name,
            order: relevantDefaultIndex?.order ?? index.order,
          };
        });
        if (IndexMappingData !== undefined) {
          relevantMapping.push({
            app_id: app?._id,
            app_name: app?.app_name,
            index_order: IndexMappingData,
          });
        }
      });
      if (relevantMapping !== undefined) {
        replace(relevantMapping);
      }
    }
  }, [GetDefaultIndexData, relevantApps, GetDefaultIndexOrderData]);

  const resetFunction = () => {
    setSolutionID(undefined);
    setApplicationIds([]);
    reset();
  };

  // Submit Functions
  // Add Default Indexes react query call Options

  // Add Default Index Order Settings
  const { mutate: mutateAddUseSetDefaultIndexOrderSettings } =
    useSetDefaultIndexOrderSettings({
      onSuccess: () => {
        message.success("Default Indexes Order Updated Successfully");
        resetFunction();
      },
      onError: (error) => {
        message.error(error.response?.data?.message ?? "Server Error");
      },
    });

  const onSubmit = (data) => {
    const { index_order } = data;
    mutateAddUseSetDefaultIndexOrderSettings({
      id: solutionID,
      data: { defaultIndexOrder: index_order },
    });
  };

  return (
    <div>
      <div className="container_aa">
        <h3>Default Index Order Settings</h3>
        <Breadcrumb
          routes={[
            { path: "/", breadcrumbName: "Dashboard" },
            {
              path: "default-index-order-settings",
              breadcrumbName: "Default Index Order Settings",
            },
          ]}
        />
      </div>
      <div className="container_ab">
        <div className="container_style_1_b">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Form.Item {...itemStyles} label="Solution">
              <Select
                placeholder="Select a Solution "
                showSearch
                loading={isLoadingSolutionsData}
                optionFilterProp="children"
                onChange={(values) => {
                  setSolutionID(values);
                  setApplicationIds([]);
                }}
                style={{ width: "65%" }}
                value={solutionID}
                filterOption={(input, option) => {
                  return (
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              >
                {SolutionsData !== undefined
                  ? SolutionsData.map((solution) => (
                      <Option
                        key={solution._id}
                        value={solution._id}
                        disabled={
                          !SolutionSlugs.find(
                            (solutionSlug) =>
                              solutionSlug.solutionSlug ===
                              solution.solution_slug,
                          )
                        }
                      >
                        {solution.solution_name}
                      </Option>
                    ))
                  : undefined}
              </Select>
            </Form.Item>
            <Form.Item {...itemStyles} label="Applications">
              <Select
                disabled={solutionID === undefined}
                placeholder="Select Applications "
                showSearch
                showArrow
                mode="multiple"
                loading={isLoadingApplicationData}
                optionFilterProp="children"
                onChange={(values) => {
                  setApplicationIds(values);
                }}
                style={{ width: "65%" }}
                value={applicationIds}
                filterOption={(input, option) => {
                  return (
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              >
                {ApplicationData !== undefined
                  ? ApplicationData.map((application) => (
                      <Option
                        key={application._id}
                        value={application._id}
                        disabled={
                          !AppList.find(
                            (App) => App.AppName === application.app_name,
                          )
                        }
                      >
                        {application?.app_name}
                      </Option>
                    ))
                  : undefined}
              </Select>
            </Form.Item>
            {fields !== undefined && fields.length > 0 ? (
              <>
                <Collapse defaultActiveKey={["1"]} expandIconPosition="end">
                  <Panel header="Index Order" key="1">
                    <IndexOrderTableElement
                      fields={fields}
                      control={control}
                      errors={errors}
                      applicationIds={applicationIds}
                    />
                  </Panel>
                </Collapse>
                <Form.Item
                  wrapperCol={{ span: 12, offset: 10 }}
                  style={{ marginTop: 15 }}
                >
                  <Space>
                    <Button onClick={resetFunction}>Cancel</Button>

                    <Button
                      type="primary"
                      disabled={
                        solutionID === undefined || fields?.length === 0
                      }
                      htmlType="submit"
                    >
                      Update
                    </Button>
                  </Space>
                </Form.Item>
              </>
            ) : null}
          </form>
        </div>
      </div>
    </div>
  );
};

export default DefaultIndexOrderSettings;
